import React, { useMemo } from 'react';
import { Alert, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useLocale } from '../../contexts/LocaleContext';
import { DataTableShell, EmptyState, LoadingState, SectionCard, StatusChip } from '../ui';
import {
  listStoreSalePolicies,
  listStoreSaleWindows,
  STORE_SALE_POLICIES_QUERY_KEY,
  STORE_SALE_WINDOWS_QUERY_KEY,
  type BrandSalePolicy,
  type StoreSaleWindow,
} from '../../services/saleWindowsApi';
import { saleWindowFloorText, saleWindowProductsText, saleWindowScheduleEvents } from './saleWindowShared';

const NS = 'store.saleWindows';

/**
 * Whether the store's scheduled-changes tab has sale windows to show: the
 * `vendor_sale_windows` flag is on for the store and at least one of its
 * brands runs the "sales only inside approved windows" rule (MOT-388). Shares
 * its queries with the MAP Pricing Data card, so the two never disagree.
 */
export const useStoreSaleWindowsVisible = (): boolean => {
  const { enabled } = useOrgFeatureFlag('vendor_sale_windows');
  const { data: policies } = useQuery<BrandSalePolicy[]>({
    queryKey: STORE_SALE_POLICIES_QUERY_KEY,
    queryFn: listStoreSalePolicies,
    enabled,
  });
  return enabled && (policies || []).length > 0;
};

/**
 * Jordan's scheduled changes from vendor-approved sale windows (MOT-370): each
 * upcoming window opening and each window closing, soonest first, with the
 * same rule, floor and verification copy as the "Approved sale windows" card on
 * MAP Pricing Data. Read-only; mounted by the Price History page only while
 * `useStoreSaleWindowsVisible()` is true.
 */
export const StoreSaleWindowSchedule: React.FC = () => {
  const { t } = useAppTranslation();
  const { formatCurrency, formatDate } = useLocale();
  const { data: policies } = useQuery<BrandSalePolicy[]>({
    queryKey: STORE_SALE_POLICIES_QUERY_KEY,
    queryFn: listStoreSalePolicies,
  });
  const { data: windows, isLoading, isError } = useQuery<StoreSaleWindow[]>({
    queryKey: STORE_SALE_WINDOWS_QUERY_KEY,
    queryFn: listStoreSaleWindows,
  });
  const events = useMemo(() => saleWindowScheduleEvents(windows || []), [windows]);

  return (
    <SectionCard dense padded title={t(`${NS}.schedule.title`)} subtitle={t(`${NS}.schedule.coverage`, { count: events.length })}>
      {(policies || []).map((b) => (
        <Typography key={b.brandId} variant="body2" sx={{ mb: 0.5 }}>
          <strong>{b.brandName}</strong>: {t(`${NS}.brandRule`)}
        </Typography>
      ))}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>{t(`${NS}.intro`)}</Typography>
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <Alert severity="error">{t(`${NS}.loadError`)}</Alert>
      ) : events.length === 0 ? (
        <EmptyState title={t(`${NS}.schedule.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
      ) : (
        <DataTableShell>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(`${NS}.schedule.when`)}</TableCell>
                <TableCell>{t(`${NS}.schedule.change`)}</TableCell>
                <TableCell>{t(`${NS}.schedule.window`)}</TableCell>
                <TableCell>{t(`${NS}.schedule.products`)}</TableCell>
                <TableCell>{t(`${NS}.schedule.floor`)}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((e) => (
                <TableRow key={e.key} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{formatDate(new Date(e.at))}</TableCell>
                  <TableCell>
                    <StatusChip
                      size="small"
                      tone={e.kind === 'opens' ? 'info' : 'neutral'}
                      label={t(`${NS}.schedule.${e.kind}`)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{e.window.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {e.window.brandName} · {e.window.dealerSpecific ? t(`${NS}.forYourStore`) : t(`${NS}.allDealers`)}
                    </Typography>
                    {!e.window.verified && e.kind === 'opens' && (
                      <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                        {t(`${NS}.verifyToPrice`, { brand: e.window.brandName })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{saleWindowProductsText(e.window, t, NS)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {e.kind === 'opens' ? saleWindowFloorText(e.window, t, NS, formatCurrency) : t(`${NS}.schedule.backToMap`)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataTableShell>
      )}
    </SectionCard>
  );
};
